/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useEffect, useRef } from "react";

import { gql, useLazyQuery } from "@apollo/client";

import {
  OCCUPATION_CODE_FRAGMENT,
  OccupationCodeFragment,
} from "graphql/fragments";
import { QueryOccupationCodesArgs } from "graphql/types";

import { PaginatedPayload } from "./shared/types";

const OCCUPATION_CODES = gql`
  query ($page: Int, $perPage: Int, $keyword: String) {
    occupationCodes(page: $page, perPage: $perPage, keyword: $keyword) {
      exportPath
      items {
        ...OccupationCodeFragment
      }
      total
    }
  }
  ${OCCUPATION_CODE_FRAGMENT}
`;

export interface OccupationCodesQuery {
  occupationCodes: PaginatedPayload<OccupationCodeFragment>;
}

type QueryArgs = Pick<QueryOccupationCodesArgs, "page" | "perPage" | "keyword">;

export const useOccupationCodesLazyQuery = () => {
  const [getOccupationCodesQuery, { data, loading: isLoading, called }] =
    useLazyQuery<OccupationCodesQuery, QueryArgs>(OCCUPATION_CODES, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "no-cache",
    });

  const occupationCodes = data?.occupationCodes.items?.filter(Boolean) ?? [];
  const total = data?.occupationCodes.total ?? 0;

  const resolveRef =
    useRef<(occupationCodes?: OccupationCodeFragment[]) => void>(undefined);

  // Backwards compatibility: if someone still awaits the promise, resolve once data arrives.
  useEffect(() => {
    if (called && !isLoading && resolveRef.current) {
      resolveRef.current(occupationCodes);
      resolveRef.current = undefined;
    }
  }, [occupationCodes, called, isLoading]);

  const getOccupationCodes = (variables?: QueryArgs) => {
    // Fire the request and do not rely on a promise chain for UI state updates
    // to avoid race conditions that can prevent pagination/search from updating.
    const exec = getOccupationCodesQuery({ variables });

    // Return a thenable for legacy callers, but UI should not depend on it
    // for state changes.
    return new Promise<OccupationCodeFragment[] | undefined>((resolve) => {
      resolveRef.current = resolve;
      // Also attach to the immediate result in case it's resolved synchronously
      Promise.resolve(exec).catch(() => undefined);
    });
  };

  return {
    getOccupationCodes,
    occupationCodes,
    total,
    isLoading,
  };
};
